/**
 * Hierarchical TanStack Query keys for the ER (Email Remediator) module.
 *
 * Mirrors lib/esa/query-keys.ts exactly: NO `mode`, NO `workspaceId` in any key.
 * Tenant isolation is enforced by `WorkspaceCacheGuard` (queryClient.clear() on
 * workspace/company switch) + the `x-workspace-id` header auto-attached by
 * `fetchApi`. Keys carry resource + filters ONLY.
 *
 * Filters go INTO the key so distinct filter combos cache separately and a
 * targeted `invalidateQueries({ queryKey: erKeys.<ns>.all() })` refreshes just
 * that namespace (never a global refetch). The domain selection is folded into
 * the filters object by each hook (the module domain selector), NOT a separate
 * key segment — so a domain switch invalidates naturally through the filters.
 */

/**
 * A filter object folded into a query key. Kept intentionally structural (not a
 * strict index signature) so the module's typed filter interfaces pass without a
 * cast. TanStack hashes it structurally.
 */
type Filters = object | undefined

export const erKeys = {
  all: () => ['er'] as const,

  // ── Reported mails (triage console) ──────────────────────────────────────
  reportedMails: {
    all: () => [...erKeys.all(), 'reported-mails'] as const,
    list: (filters?: Filters) =>
      [...erKeys.reportedMails.all(), 'list', filters ?? {}] as const,
    /** Status-bucket counts driving the NB card-list status-sidebar. */
    statuses: (filters?: Filters) =>
      [...erKeys.reportedMails.all(), 'statuses', filters ?? {}] as const,
    /** Server-side rail-bucket counts (console status rail, Wave 5a). */
    railCounts: (filters?: Filters) =>
      [...erKeys.reportedMails.all(), 'rail-counts', filters ?? {}] as const,
    detail: (id: string) =>
      [...erKeys.reportedMails.all(), 'detail', id] as const,
    children: (id: string) =>
      [...erKeys.reportedMails.all(), 'children', id] as const,
    timeline: (id: string) =>
      [...erKeys.reportedMails.all(), 'timeline', id] as const,
  },

  // ── Dashboard (aggregate-only) ───────────────────────────────────────────
  dashboard: {
    all: () => [...erKeys.all(), 'dashboard'] as const,
    overview: (filters?: Filters) =>
      [...erKeys.dashboard.all(), 'overview', filters ?? {}] as const,
    statusBuckets: (filters?: Filters) =>
      [...erKeys.dashboard.all(), 'status-buckets', filters ?? {}] as const,
    lastHour: (filters?: Filters) =>
      [...erKeys.dashboard.all(), 'last-hour', filters ?? {}] as const,
    remediationTrend: (filters?: Filters) =>
      [...erKeys.dashboard.all(), 'remediation-trend', filters ?? {}] as const,
    authFailures: (filters?: Filters) =>
      [...erKeys.dashboard.all(), 'auth-failures', filters ?? {}] as const,
    analysis: (filters?: Filters) =>
      [...erKeys.dashboard.all(), 'analysis', filters ?? {}] as const,
    flaggedBreakdown: (filters?: Filters) =>
      [...erKeys.dashboard.all(), 'flagged-breakdown', filters ?? {}] as const,
    sourceBreakdown: (filters?: Filters) =>
      [...erKeys.dashboard.all(), 'source-breakdown', filters ?? {}] as const,
    remediationBreakdown: (filters?: Filters) =>
      [...erKeys.dashboard.all(), 'remediation-breakdown', filters ?? {}] as const,
    antiphishOverrides: (filters?: Filters) =>
      [...erKeys.dashboard.all(), 'antiphish-overrides', filters ?? {}] as const,
    userGroupOverview: (filters?: Filters) =>
      [...erKeys.dashboard.all(), 'user-group-overview', filters ?? {}] as const,
    topAffectedDomains: (filters?: Filters) =>
      [...erKeys.dashboard.all(), 'top-affected-domains', filters ?? {}] as const,
    monitoringDomains: (filters?: Filters) =>
      [...erKeys.dashboard.all(), 'monitoring-domains', filters ?? {}] as const,
    threatLogsDashboard: (filters?: Filters) =>
      [...erKeys.dashboard.all(), 'threat-logs', filters ?? {}] as const,
  },

  // ── Search & Remediate ───────────────────────────────────────────────────
  searchRemediate: {
    all: () => [...erKeys.all(), 'search-remediate'] as const,
    list: (filters?: Filters) =>
      [...erKeys.searchRemediate.all(), 'list', filters ?? {}] as const,
    detail: (id: string) =>
      [...erKeys.searchRemediate.all(), 'detail', id] as const,
    progress: (id: string) =>
      [...erKeys.searchRemediate.all(), 'progress', id] as const,
    messages: (id: string, filters?: Filters) =>
      [...erKeys.searchRemediate.all(), 'messages', id, filters ?? {}] as const,
    remediationJobs: (id: string) =>
      [...erKeys.searchRemediate.all(), 'remediation-jobs', id] as const,
  },

  // ── Threat logs ──────────────────────────────────────────────────────────
  threatLogs: {
    all: () => [...erKeys.all(), 'threat-logs'] as const,
    list: (filters?: Filters) =>
      [...erKeys.threatLogs.all(), 'list', filters ?? {}] as const,
    detail: (id: string) =>
      [...erKeys.threatLogs.all(), 'detail', id] as const,
  },

  // ── Reports ──────────────────────────────────────────────────────────────
  reports: {
    all: () => [...erKeys.all(), 'reports'] as const,
    reportedMails: (filters?: Filters) =>
      [...erKeys.reports.all(), 'reported-mails', filters ?? {}] as const,
    threatLogs: (filters?: Filters) =>
      [...erKeys.reports.all(), 'threat-logs', filters ?? {}] as const,
    remediationStatusChanges: (filters?: Filters) =>
      [
        ...erKeys.reports.all(),
        'remediation-status-changes',
        filters ?? {},
      ] as const,
    remediationDispositions: (filters?: Filters) =>
      [
        ...erKeys.reports.all(),
        'remediation-dispositions',
        filters ?? {},
      ] as const,
  },

  // ── Leaderboard ──────────────────────────────────────────────────────────
  leaderboard: {
    all: () => [...erKeys.all(), 'leaderboard'] as const,
    list: (filters?: Filters) =>
      [...erKeys.leaderboard.all(), 'list', filters ?? {}] as const,
    stats: (filters?: Filters) =>
      [...erKeys.leaderboard.all(), 'stats', filters ?? {}] as const,
    remediationStats: (filters?: Filters) =>
      [...erKeys.leaderboard.all(), 'remediation-stats', filters ?? {}] as const,
  },

  // ── Logs (email / notification / banner / AI action) ─────────────────────
  emailLogs: {
    all: () => [...erKeys.all(), 'email-logs'] as const,
    list: (filters?: Filters) =>
      [...erKeys.emailLogs.all(), 'list', filters ?? {}] as const,
  },
  notificationLogs: {
    all: () => [...erKeys.all(), 'notification-logs'] as const,
    list: (filters?: Filters) =>
      [...erKeys.notificationLogs.all(), 'list', filters ?? {}] as const,
  },
  bannerLogs: {
    all: () => [...erKeys.all(), 'banner-logs'] as const,
    list: (filters?: Filters) =>
      [...erKeys.bannerLogs.all(), 'list', filters ?? {}] as const,
    users: (id: string) => [...erKeys.bannerLogs.all(), 'users', id] as const,
  },
  aiActionLogs: {
    all: () => [...erKeys.all(), 'ai-action-logs'] as const,
    list: (filters?: Filters) =>
      [...erKeys.aiActionLogs.all(), 'list', filters ?? {}] as const,
  },

  // ── Settings — workspace policy + reporter messages/actions/points ───────
  settings: () => [...erKeys.all(), 'settings'] as const,
  policy: () => [...erKeys.all(), 'policy'] as const,
  reporterMessages: () => [...erKeys.all(), 'reporter-messages'] as const,
  addinActions: () => [...erKeys.all(), 'addin-actions'] as const,
  reporterPoints: {
    all: () => [...erKeys.all(), 'reporter-points'] as const,
    detail: (filters?: Filters) =>
      [...erKeys.reporterPoints.all(), filters ?? {}] as const,
  },

  // ── Add-in white-label branding (Wave C · Ca4 / Cb) ──────────────────────────
  //
  // Cb3 asked for `['er','addin-branding',mode,workspaceId]`, but the Live/Demo
  // mode-keying was codemodded out repo-wide ("Production is the only mode";
  // scripts/codemods/strip-api-mode.ts) — `useApiModeStore` no longer exists, and
  // every erKeys node is mode-less. Tenant isolation rides the same rails as the
  // rest of ER: `WorkspaceCacheGuard` (queryClient.clear() on workspace/company
  // switch) + the `x-workspace-id` header `fetchApi` auto-attaches. So this key
  // stays mode-less + workspace-less to match the module convention exactly.
  addinBranding: {
    all: () => [...erKeys.all(), 'addin-branding'] as const,
    settings: () => [...erKeys.addinBranding.all(), 'settings'] as const,
  },

  // ── Catalogs (dispositions / event-actions / trusted-domains / integrations) ─
  dispositions: {
    all: () => [...erKeys.all(), 'dispositions'] as const,
    list: (filters?: Filters) =>
      [...erKeys.dispositions.all(), 'list', filters ?? {}] as const,
  },
  eventActions: {
    all: () => [...erKeys.all(), 'event-actions'] as const,
    list: (filters?: Filters) =>
      [...erKeys.eventActions.all(), 'list', filters ?? {}] as const,
  },
  trustedDomains: {
    all: () => [...erKeys.all(), 'trusted-domains'] as const,
    list: (filters?: Filters) =>
      [...erKeys.trustedDomains.all(), 'list', filters ?? {}] as const,
  },
  integrations: {
    all: () => [...erKeys.all(), 'integrations'] as const,
    list: (filters?: Filters) =>
      [...erKeys.integrations.all(), 'list', filters ?? {}] as const,
  },

  // ── Templates + default-template settings ────────────────────────────────
  templates: {
    all: () => [...erKeys.all(), 'templates'] as const,
    list: (filters?: Filters) =>
      [...erKeys.templates.all(), 'list', filters ?? {}] as const,
    detail: (id: string) => [...erKeys.templates.all(), 'detail', id] as const,
    settings: (filters?: Filters) =>
      [...erKeys.templates.all(), 'settings', filters ?? {}] as const,
  },

  // ── Per-domain notification-template overrides (V4 Domain tier) ───────────
  notificationTemplates: {
    all: () => [...erKeys.all(), 'notification-templates'] as const,
    list: (domainId?: string | null) =>
      [...erKeys.notificationTemplates.all(), 'list', domainId ?? null] as const,
  },
}
